import { useCallback, useEffect, useRef, useState } from "react";
import * as staticDataService from "services/staticDataService";
import {
  useRetrievePreferencesQuery,
  useUpdatePreferencesMutation,
} from "services/userService";

const STORAGE_KEY = "marmalade_emoji_tags";

const useEmojiTags = () => {
  const isFeatureEnabled = staticDataService.useFeatureFlag(
    "submission-search-emoji-tags",
    { default: false }
  );

  const {
    data: serverPreferences,
    isLoading: isLoadingPreferences,
    isSuccess: isPreferencesLoaded,
  } = useRetrievePreferencesQuery(undefined, {
    skip: !isFeatureEnabled,
  });
  const [updatePreferences] = useUpdatePreferencesMutation();

  const [emojiTagsMap, setEmojiTagsMap] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  const hasSyncedFromServer = useRef(false);

  useEffect(() => {
    if (
      isPreferencesLoaded &&
      serverPreferences &&
      !hasSyncedFromServer.current
    ) {
      const serverTags =
        serverPreferences.emojiTags || serverPreferences.emoji_tags || {};
      setEmojiTagsMap(serverTags);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(serverTags));
      } catch {
        // Ignore localStorage errors
      }
      hasSyncedFromServer.current = true;
    }
  }, [isPreferencesLoaded, serverPreferences]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(emojiTagsMap));
    } catch {
      // Ignore localStorage errors
    }
  }, [emojiTagsMap]);

  const saveTimeoutRef = useRef(null);
  const serverPreferencesRef = useRef(serverPreferences);
  useEffect(() => {
    serverPreferencesRef.current = serverPreferences;
  }, [serverPreferences]);

  const saveToBackend = useCallback(
    (newTags) => {
      if (!isFeatureEnabled) return;
      if (!isPreferencesLoaded) return;
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        updatePreferences({
          ...(serverPreferencesRef.current || {}),
          emoji_tags: newTags,
        }).catch(() => {});
      }, 500);
    },
    [isFeatureEnabled, isPreferencesLoaded, updatePreferences]
  );

  const getTagsForSubmission = useCallback(
    (submissionId) => {
      return emojiTagsMap[submissionId] || [];
    },
    [emojiTagsMap]
  );

  const toggleTag = useCallback(
    (submissionId, emoji) => {
      setEmojiTagsMap((prev) => {
        const currentTags = prev[submissionId] || [];
        const hasEmoji = currentTags.includes(emoji);

        let updatedMap;
        if (hasEmoji) {
          const { [submissionId]: _, ...rest } = prev;
          updatedMap = rest;
        } else {
          updatedMap = {
            ...prev,
            [submissionId]: [emoji],
          };
        }

        saveToBackend(updatedMap);
        return updatedMap;
      });
    },
    [saveToBackend]
  );

  return {
    emojiTagsMap,
    getTagsForSubmission,
    toggleTag,
    isLoading: isLoadingPreferences,
  };
};

export default useEmojiTags;
